import React, { useState } from "react";
import { Modal, Select, Button, DatePicker } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Option } = Select;
const { RangePicker } = DatePicker;

/* ================= TYPES ================= */
interface FilterModalProps {
    open: boolean;
    onClose: () => void;
}

/* ================= FIELD CONFIG ================= */

/* ================= COMPONENT ================= */
export default function FilterModal({ open, onClose }: FilterModalProps) {
    const { t } = useTranslation();

    const FIELD_OPTIONS = {
        customer_type: {
            label: t('customer.customer_type'),
            type: "select",
            options: [
                { label: t('customer.vip'), value: "vip" },
                { label: t('customer.normal'), value: "normal" },
            ],
        },
        job: {
            label: t('customer.career'),
            type: "select",
            options: [
                { label: "IT", value: "it" },
                { label: "Marketing", value: "marketing" },
            ],
        },
        birthday: {
            label: t('customer.dob'),
            type: "date-range",
            options: [],
        },
    };
    const [rows, setRows] = useState<Array<{ field?: string; condition?: string; value?: any }>>([
        { field: undefined, condition: undefined, value: undefined },
    ]);

    /* ===== HANDLE CHANGE ===== */
    const handleChange = (index: number, key: string, value: any) => {
        const newRows = [...rows];
        newRows[index][key as keyof typeof newRows[0]] = value;

        // đổi field → reset value
        if (key === "field") {
            newRows[index].value = undefined;
        }

        setRows(newRows);
    };

    /* ===== ADD / REMOVE ===== */
    const addRow = () => {
        setRows([
            ...rows,
            { field: undefined, condition: undefined, value: undefined },
        ]);
    };

    const removeRow = (index: number) => {
        const newRows = [...rows];
        newRows.splice(index, 1);
        setRows(newRows);
    };

    /* ===== RENDER VALUE ===== */
    const renderValue = (row: typeof rows[0], index: number) => {
        const field = row.field ? FIELD_OPTIONS[row.field as keyof typeof FIELD_OPTIONS] : undefined;

        if (!field) {
            return (
                <Select disabled placeholder={t('customer.select_field_first')} className="w-full" />
            );
        }

        // 👉 Ngày sinh → date range
        if (field.type === "date-range") {
            return (
                <RangePicker
                    value={row.value}
                    onChange={(val) => handleChange(index, "value", val)}
                    className="w-full"
                />
            );
        }

        // 👉 Các field khác → select
        return (
            <Select
                value={row.value}
                onChange={(val) => handleChange(index, "value", val)}
                options={field.options || []}
                placeholder={t('customer.select_value')}
                allowClear
                className="w-full"
            />
        );
    };

    const handleReset = () => {
        setRows([
            { field: undefined, condition: undefined, value: undefined },
        ]);
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={800}
            title={t('customer.setup_conditions')}
            className="
                dark:[&_.ant-modal-content]:bg-[#1f1f1f] 
                dark:[&_.ant-modal-header]:bg-[#1f1f1f]
                dark:[&_.ant-modal-title]:text-white
            "
        >
            {/* LOGIC */}
            <div className="grid md:grid-cols-[1fr_1fr] !gap-4 mb-4">
                <div className="flex flex-col">
                    <span className="text-xs text-gray-400 mb-1">{t('customer.logic_combine')}</span>
                    <Select defaultValue="AND" className="w-full">
                        <Option value="AND">{t('customer.satisfy_all')}</Option>
                        <Option value="OR">{t('customer.or_condition')}</Option>
                    </Select>
                </div>

                <div className="flex flex-col">
                    <span className="text-xs text-gray-400 mb-1">{t('customer.select_saved_filter')}</span>
                    <Select placeholder={t('customer.select_saved_filter')} className="w-full" />
                </div>
            </div>

            {/* TABLE */}
            <div className="border rounded-md overflow-hidden">

                {/* HEADER */}
                <div className="
                    hidden md:grid
                    md:grid-cols-[2fr_2fr_3fr_40px] 
                    gap-2 bg-[#3e91ab] text-white px-3 py-2 text-sm
                ">
                    <div>{t('customer.field')}</div>
                    <div>{t('customer.condition')}</div>
                    <div>{t('customer.value')}</div>
                    <div></div>
                </div>

                {/* ROWS */}
                {rows.map((row, index) => (
                    <div
                        key={index}
                        className="
                            grid 
                            grid-cols-1 
                            md:grid-cols-[2fr_2fr_3fr_40px] 
                            gap-2 px-3 py-2 border-t items-center
                        "
                    >
                        {/* FIELD */}
                        <div className="flex flex-col md:block">
                            <span className="md:hidden text-xs text-gray-400 mb-1">{t('customer.field')}</span>
                            <Select
                                value={row.field}
                                onChange={(val) => handleChange(index, "field", val)}
                                placeholder={t('customer.select_field')}
                                className="w-full"
                                options={Object.keys(FIELD_OPTIONS).map((key) => ({
                                    label: FIELD_OPTIONS[key as keyof typeof FIELD_OPTIONS].label,
                                    value: key,
                                }))}
                            />
                        </div>

                        <div className="flex flex-col md:block">
                            <span className="md:hidden text-xs text-gray-400 mb-1">{t('customer.condition')}</span>
                            {/* CONDITION */}
                            <Select
                                value={row.condition}
                                onChange={(val) => handleChange(index, "condition", val)}
                                placeholder={t('customer.condition')}
                                options={[
                                    { label: t('customer.equals'), value: "=" },
                                    { label: t('customer.not_empty'), value: "not_empty" },
                                ]}
                                className="w-full"
                            />
                        </div>

                        <div className="flex flex-col md:block">
                            <span className="md:hidden text-xs text-gray-400 mb-1">{t('customer.value')}</span>
                            {/* VALUE */}
                            {renderValue(row, index)}
                        </div>

                        <div className="flex flex-col md:block">
                            {/* DELETE */}
                            <div className="flex justify-center">
                                <DeleteOutlined
                                    onClick={() => removeRow(index)}
                                    className="text-red-500 cursor-pointer"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ADD */}
            <div
                onClick={addRow}
                className="mt-3 text-blue-500 cursor-pointer flex items-center gap-2"
            >
                <PlusOutlined />
                {t('customer.add_condition')}
            </div>

            {/* FOOTER */}
            <div className="flex justify-between items-center mt-6">

                {/* LEFT: RESET */}
                <Button onClick={handleReset}>
                    {t('customer.reset_filter')}
                </Button>

                {/* RIGHT */}
                <div className="flex gap-2">
                    <Button onClick={onClose}>{t('customer.cancel')}</Button>
                    <Button type="primary">{t('customer.apply')}</Button>
                    <Button type="primary">{t('customer.apply_create_group')}</Button>
                </div>
            </div>
        </Modal>
    );
}